import React from 'react';
import { Link } from 'react-router-dom';
import { Award, ArrowRight, Users, Factory } from 'lucide-react';
import { clients } from '../data/mockData';
import SEO from '../components/SEO';

const Clients = () => {
  return (
    <div className="min-h-screen bg-[#f9fafb] overflow-hidden">
      <SEO
        title="Our Clients | Stallion Bulktech Industrial Solutions"
        description="Discover the industries and clients that trust Stallion Bulktech for machine guarding, dust suppression, and wear resistance solutions across demanding industrial environments."
        path="/clients"
      />
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-20 overflow-hidden">
        {/* Animated Background Layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-cyan-50/30 to-blue-50/30">
          <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-6">
            <Users className="h-4 w-4 text-amber-600" />
            <p className="text-amber-600 text-sm font-medium">Our Esteemed Clients</p>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Trusted by Leading
            <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-size-200 animate-gradient">
              Organizations
            </span>
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            As a result of our continuous efforts to meet the challenging demands of the industry, we have received continuous patronage from some of the eminent corporate in India and abroad.
          </p>
        </div>
      </section>

      {/* Trust Badge */}
      <section className="py-12 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="group relative w-full max-w-xs">
              {/* Glow effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl p-5 hover:border-cyan-300 transition-all duration-500 transform group-hover:-translate-y-2 group-hover:shadow-2xl flex flex-col items-center justify-center text-center">
                {/* Icon */}
                <div className="w-11 h-11 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-3 shadow-lg shadow-cyan-500/30 group-hover:scale-110 transition-transform duration-300">
                  <Factory className="h-5 w-5 text-white" />
                </div>
                {/* Number */}
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent leading-none mb-2 transform group-hover:scale-110 transition-transform duration-300">
                  25000+
                </div>
                {/* Label */}
                <div className="text-gray-600 text-xs md:text-sm font-medium">
                  Units Installed across industries
                </div>
                {/* Decorative element */}
                <div className="absolute top-3 right-3 w-6 h-6 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-full blur-lg group-hover:scale-150 transition-transform duration-500" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Grid */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-white to-[#f9fafb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {clients.map((client, index) => (
              <div
                key={client.name}
                className="group relative"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative bg-white border border-gray-200 rounded-2xl p-3 sm:p-4 flex items-center justify-center hover:border-cyan-300 transition-all duration-500 transform hover:-translate-y-1 hover:shadow-xl h-28 sm:h-32">
                  <img
                    src={client.logo}
                    alt={client.name}
                    data-testid={`client-logo-${index}`}
                    className="max-h-full max-w-full w-auto object-contain opacity-90 group-hover:opacity-100 transform group-hover:scale-110 transition-all duration-500"
                  />
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-gray-400 mt-12">
            *All Trademarks, Logos and Brand Names are the property of their Respective Owners.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl p-8 hover:border-cyan-300 transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl h-48 flex flex-col justify-center items-center">
                <div className="h-16 flex items-center justify-center text-5xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent transform group-hover:scale-110 transition-transform duration-300">500+</div>
                <div className="h-12 flex items-center justify-center text-gray-600 text-center mt-2">Industrial Facilities Secured</div>
              </div>
            </div>
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl p-8 hover:border-cyan-300 transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl h-48 flex flex-col justify-center items-center">
                <div className="h-16 flex items-center justify-center text-5xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent transform group-hover:scale-110 transition-transform duration-300">97%</div>
                <div className="h-12 flex items-center justify-center text-gray-600 text-center mt-2">Client Retention Rate</div>
              </div>
            </div>
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl p-8 hover:border-cyan-300 transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl h-48 flex flex-col justify-center items-center">
                <div className="h-16 flex items-center justify-center text-5xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent transform group-hover:scale-110 transition-transform duration-300">360°</div>
                <div className="h-12 flex items-center justify-center text-gray-600 text-center mt-2">Zero-Contact Guarding Systems</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Diversity */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#f9fafb] to-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-white to-gray-50/50 border border-gray-200 rounded-2xl p-6 md:p-12 shadow-xl">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Industry Leadership</h2>
              <p className="text-base text-gray-600 max-w-2xl mx-auto">
                Our clients represent diverse sectors including Steel, Automobile, Cement, Chemical, Food & Beverage, Pharma, Power, Mining, Textiles, and more.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-xl p-8 hover:border-cyan-300 transition-all hover:shadow-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Why They Choose Us</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-cyan-600 rounded-full flex-shrink-0 mt-2" />
                      <span>ISO India compliant safety solutions</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-cyan-600 rounded-full flex-shrink-0 mt-2" />
                      <span>Customized designs for specific requirements</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-cyan-600 rounded-full flex-shrink-0 mt-2" />
                      <span>Maintenance-friendly and durable products</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-cyan-600 rounded-full flex-shrink-0 mt-2" />
                      <span>Expert technical support and consultation</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-xl p-8 hover:border-cyan-300 transition-all hover:shadow-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Our Commitment</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-cyan-600 rounded-full flex-shrink-0 mt-2" />
                      <span>Continuous innovation in safety solutions</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-cyan-600 rounded-full flex-shrink-0 mt-2" />
                      <span>Quality products using ISI standard materials</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-cyan-600 rounded-full flex-shrink-0 mt-2" />
                      <span>Timely delivery and installation support</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-cyan-600 rounded-full flex-shrink-0 mt-2" />
                      <span>Long-term partnership approach</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-size-200 animate-gradient" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Join Our Growing List of Satisfied Clients
          </h2>
          <p className="text-base md:text-xl text-white/90 mb-8 md:mb-10">
            Partner with us for engineered machine guards, dust suppression, and wear resistance solutions, including manufacturing, supply, and on-site service.
          </p>
          <Link
            to="/contact"
            className="group relative inline-flex items-center space-x-2"
          >
            <div className="absolute inset-0 bg-white rounded-xl blur opacity-50 group-hover:opacity-100 transition-opacity" />
            <div className="relative flex items-center space-x-2 bg-white text-cyan-600 px-10 py-5 rounded-xl font-bold text-lg transform group-hover:scale-105 transition-all shadow-2xl">
              <span>Contact Us Today</span>
              <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform" />
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Clients;
